import type { Character, SequenceItem } from '@/types/screenplay';

// A display statistic only: never changes audio chunking, stored blocks or text.
export function countTextSentences(text: string, type: SequenceItem['type'] = 'dialogue'): number {
  const normalized = text.replace(/\s+/gu, ' ').trim();
  if (!/[\p{L}\p{N}]/u.test(normalized)) return 0;
  if (type === 'parenthetical') return 0;
  if (type === 'scene-heading' || type === 'transition') return 1;
  const Segmenter = (Intl as unknown as { Segmenter?: new (locale: string, options: { granularity: 'sentence' }) =>
    { segment: (text: string) => Iterable<{ segment: string }> } }).Segmenter;
  const parts = Segmenter ? [...new Segmenter('en', { granularity: 'sentence' }).segment(normalized)].map(s => s.segment.trim())
    : normalized.match(/[^.!?。！？]+(?:[.!?。！？]+["'”’)]*(?=\s|$)|$)/gu) ?? [normalized];
  const merged: string[] = [];
  for (const part of parts) {
    const previous = merged[merged.length - 1];
    if (previous && /\b(?:Dr|Mr|Mrs|Ms|Prof|Capt|Lt|Sgt|Gen|St)\.$/iu.test(previous)) merged[merged.length - 1] += ` ${part}`;
    else merged.push(part);
  }
  return merged.filter(part => /[\p{L}\p{N}]/u.test(part)).length;
}

function spokenSource(block: SequenceItem): string {
  const silent = (block.intonation?.spans ?? []).filter(span =>
    (span.gemini?.unspoken || span.gemini?.pauseMarker) && Number.isInteger(span.start) && Number.isInteger(span.end) &&
    span.start >= 0 && span.end > span.start && span.end <= block.text.length);
  // Mask, rather than splice, so overlapping ranges and source offsets remain stable.
  const characters = block.text.split('');
  for (const span of silent) characters.fill(' ', span.start, span.end);
  return characters.join('');
}

export function characterSentenceCounts(characters: Character[]): Record<string, number> {
  const counts = new Map<string, number>();
  const add = (role: string, text: string, type: SequenceItem['type']) => counts.set(role, (counts.get(role) ?? 0) + countTextSentences(text, type));
  const sequence = characters.find(c => c.name === 'SCREENPLAY_SEQUENCE')?.sequence;
  if (sequence?.length) {
    for (const block of sequence) {
      if (block.type === 'parenthetical') continue;
      add(block.type === 'dialogue' && block.character ? block.character : 'SCENE NARRATOR', spokenSource(block), block.type);
    }
  } else {
    for (const character of characters) {
      if (character.name === 'SCREENPLAY_SEQUENCE') continue;
      for (const text of character.dialogue ?? []) add(character.name, text, character.name === 'SCENE NARRATOR' ? 'action' : 'dialogue');
    }
  }
  return Object.fromEntries(counts);
}
